import { ImageAsset } from "@/components/ImageAsset";
import { PlayIconCircle } from "@/components/icons";
import { BandSendMessageButton } from "@/components/music/BandSendMessageButton";
import { nextTrackState, playerPlaylistState } from "@/context/states";
import { responsiveImage } from "@/lib/helper";
import { basierCirleBold } from "@/styles/fonts";
import { CardActionArea } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import theme from "../styles/theme";

export const BandIndexGridBand = ({ band }) => {
  const router = useRouter();
  const [playerPlaylist, setPlayerPlaylist] =
    useRecoilState(playerPlaylistState);
  const [nextTrack, setNextTrack] = useRecoilState(nextTrackState);

  const handlePlayBand = (event) => {
    event.preventDefault();

    setPlayerPlaylist(band.songs);
    setNextTrack(band.songs[0]);
  };

  return (
    <Card elevation={0} sx={BandIndexTableBandStyles}>
      <Grid container>
        <Grid item xs={12}>
          <Box className="image-play-button-wrapper">
            <Link href={`/musik/${band?.slug}`}>
              <ImageAsset
                url={responsiveImage(band?.photo?.formats, 600)}
                layout="fill"
              />
              <Box className="overlay"></Box>
            </Link>
            {band.songs.length > 0 && (
              <IconButton
                className="play-button"
                component="div"
                onClick={handlePlayBand}
              >
                <SvgIcon>
                  <PlayIconCircle />
                </SvgIcon>
              </IconButton>
            )}
          </Box>
        </Grid>
        <CardContent className="card-content">
          <Grid item xs={12}>
            <CardActionArea
              className="band-wrapper"
              href={`/musik/${band?.slug}`}
              component={Link}
              disableRipple
            >
              <Grid container alignItems="center" sx={{ height: "100%" }}>
                <Grid item xs={12}>
                  <Typography className="band-name">{band.name}</Typography>
                  <Typography variant="body2" className="band-genre">
                    {band.genres?.[0]?.title} aus {band.zipcode?.label}
                  </Typography>
                </Grid>
              </Grid>
            </CardActionArea>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              <Grid item xs="auto" className="send-message-button-wrapper">
                <BandSendMessageButton
                  title={
                    band?.allow_messages && band?.verified
                      ? "Nachricht schreiben"
                      : "Diese Band möchte keine Nachrichten empfangen"
                  }
                  href={`/secure/chat/new?band=${band?.slug}`}
                  disabled={!band?.allow_messages || !band?.verified}
                  onClick={() => {
                    router.push(`/secure/chat/new?band=${band?.slug}`);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Grid>
    </Card>
  );
};

const BandIndexTableBandStyles = {
  transition: "all .2s ease-in-out",
  background: grey[200],
  height: "100%",
  position: "relative",
  overflow: "visible",
  "&:hover": {
    backgroundColor: "#6d5acc",
    "& .band-name": {
      color: `${theme.palette.common.white} !important`,
    },
    "& .image-play-button-wrapper": {
      "& .overlay::after": {
        opacity: "1",
        background:
          "linear-gradient(0deg, rgba(63,50,131,0.85) 0%, rgba(126,105,238,0.85) 100%)",
      },
    },
    "& .send-message-button-wrapper": {
      "& .MuiIconButton-root": {
        borderColor: theme.palette.common.white,
      },
      "& .MuiSvgIcon-root": {
        color: theme.palette.common.white,
      },
    },
  },
  "& .image-play-button-wrapper": {
    aspectRatio: "16 / 9",
    position: "relative",
    "& .overlay": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
    "& .overlay::after": {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      content: "''",
      opacity: "0",
      background:
        "linear-gradient(0deg, rgba(63,50,131,0.85) 0%, rgba(126,105,238,0.85) 100%)",
      transition: "all .2s ease-in-out",
    },
    "&:hover": {
      "& .MuiSvgIcon-root": {
        color: "#F49C2E!important",
      },
    },
  },
  "& .play-button": {
    position: "absolute",
    top: "0",
    right: "0",
    bottom: "0",
    left: "0",
    mx: "auto",
    "& .MuiSvgIcon-root": {
      fontSize: 64,
      color: theme.palette.common.white,
      zIndex: "30",
      transition: "all .2s ease-in-out",
      [theme.breakpoints.up("sm")]: {
        fontSize: 72,
      },
    },
  },
  "& .card-content": {
    width: "100%",
    padding: "1rem",
  },
  "& .band-wrapper": {
    marginBottom: "1rem",
    textAlign: "center",
    "&:hover": {
      "& .MuiCardActionArea-focusHighlight": {
        opacity: "0",
      },
    },
  },
  "& .band-name": {
    color: theme.palette.secondary.main,
    fontSize: ".9375rem",
    fontWeight: basierCirleBold.style.fontWeight,
    fontFamily: basierCirleBold.style.fontFamily,
    textTransform: "uppercase",
    textOverflow: "ellipsis",
    width: "100%",
    overflow: "hidden",
    lineClamp: "2",
    display: "-webkit-box",
    "-webkit-line-clamp": "2",
    "-webkit-box-orient": "vertical",
    [theme.breakpoints.up("sm")]: {
      mb: 0.5,
      fontSize: "1.375rem",
    },
    "&:hover": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  "& .band-genre": {
    textTransform: "uppercase",
    color: grey[900],
    fontSize: ".625rem",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
    overflow: "hidden",
    [theme.breakpoints.up("sm")]: {
      fontSize: "1rem",
    },
  },
};
